import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../lib/api';
import { Card, CardContent } from '../../components/ui/Card';
import Badge from '../../components/ui/Badge';
import Button from '../../components/ui/Button';
import { Calendar, Plus, Trash2 } from 'lucide-react';

export default function AdminWorkingDaysPage() {
  const queryClient = useQueryClient();
  const [newHoliday, setNewHoliday] = useState('');
  const [newHolidayDesc, setNewHolidayDesc] = useState('');

  const { data: calendar } = useQuery({
    queryKey: ['earningCalendar'],
    queryFn: () => api.get('/settings/calendar').then(r => r.data),
  });

  const addHolidayMutation = useMutation({
    mutationFn: (data: { date: string; description: string }) =>
      api.post('/settings/calendar/holiday', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['earningCalendar'] });
      setNewHoliday('');
      setNewHolidayDesc('');
    },
  });

  const removeHolidayMutation = useMutation({
    mutationFn: (date: string) => api.delete(`/settings/calendar/holiday/${date}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['earningCalendar'] }),
  });

  const holidays = calendar?.filter((d: any) => d.isHoliday) || [];
  const workdays = calendar?.filter((d: any) => d.isWorkday && !d.isHoliday) || [];
  const weekends = calendar?.filter((d: any) => !d.isWorkday && !d.isHoliday) || [];

  const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  return (
    <div className="space-y-6 animate-fade-in">
      <div><h1 className="text-2xl font-bold text-white">Working Days &amp; Holidays</h1><p className="text-sm text-slate-400">Configure earning days, weekends, and holidays</p></div>

      <Card><CardContent>
        <h2 className="text-sm font-semibold text-slate-200 mb-3">Week Schedule</h2>
        <div className="space-y-2">{days.map((day, i) => (
          <div key={day} className="flex items-center justify-between p-3 bg-white/[0.02] rounded-lg">
            <span className="text-sm font-medium text-white">{day}</span>
            <Badge status={i < 5 ? 'ACTIVE' : 'INACTIVE'} />
          </div>
        ))}</div>
      </CardContent></Card>

      <Card><CardContent>
        <h2 className="text-sm font-semibold text-slate-200 mb-3">Add Holiday</h2>
        <div className="flex gap-2 mb-4">
          <input type="date" value={newHoliday} onChange={(e) => setNewHoliday(e.target.value)} className="flex-1 px-3 py-2 border border-white/[0.06] rounded-lg text-sm bg-white/[0.02] text-white" />
          <input type="text" value={newHolidayDesc} onChange={(e) => setNewHolidayDesc(e.target.value)} placeholder="Description (optional)" className="flex-1 px-3 py-2 border border-white/[0.06] rounded-lg text-sm bg-white/[0.02] text-white placeholder-slate-500" />
          <Button size="sm" onClick={() => newHoliday && addHolidayMutation.mutate({ date: newHoliday, description: newHolidayDesc })} disabled={!newHoliday}>
            <Plus className="w-4 h-4 mr-1" />Add
          </Button>
        </div>
        {holidays.length === 0 ? (
          <p className="text-sm text-slate-400 py-2">No holidays configured</p>
        ) : (
          <div className="space-y-2">{holidays.map((h: any) => (
            <div key={h.id} className="flex items-center justify-between p-3 bg-red-500/10 rounded-lg">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-red-400" />
                <div>
                  <p className="text-sm font-medium text-white">{new Date(h.date).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
                  {h.description && <p className="text-xs text-slate-400">{h.description}</p>}
                </div>
              </div>
              <Button variant="danger" size="sm" onClick={() => removeHolidayMutation.mutate(new Date(h.date).toISOString().split('T')[0])}>
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          ))}</div>
        )}
        <div className="mt-4 grid grid-cols-3 gap-4 text-sm">
          <div className="p-3 bg-emerald-500/10 rounded-lg text-center"><p className="text-slate-400">Working Days</p><p className="text-xl font-bold text-emerald-400">{workdays.length}</p></div>
          <div className="p-3 bg-white/[0.02] rounded-lg text-center"><p className="text-slate-400">Weekends</p><p className="text-xl font-bold text-slate-300">{weekends.length}</p></div>
          <div className="p-3 bg-red-500/10 rounded-lg text-center"><p className="text-slate-400">Holidays</p><p className="text-xl font-bold text-red-400">{holidays.length}</p></div>
        </div>
      </CardContent></Card>
    </div>
  );
}
